import { default as dayjs } from "dayjs";
import XLSXC from "xlsx-color";
import { getProccesTypesList } from "../components/getProccesTypesList";

const keywords = ["tutar", "kar", "komisyonu", "komisyon"];

const containsKeyword = (key: string): boolean => {
  return keywords.some((keyword) => key.toLowerCase().includes(keyword));
};
const isNumeric = (value: string, key: string): boolean => {
  if (key === "Banka Kodu" || key === "MasterPass Terminal Grup Id" || key === "Kartın Banka Kodu" || key === "Kart ID" || key === "Kart Numarası") return false;
  // Hem nokta hem de virgül içeren sayıları kabul eden regex
  const numericRegex = /^-?[0-9]+([.,][0-9]+)?$/;
  if (
    key === "Vergi Numarası" &&
    value.startsWith("0") &&
    numericRegex.test(value)
  ) {
    return false;
  }
  return numericRegex.test(value);
};

interface CellStyle {
  fill?: {
    patternType: string;
    fgColor: {
      rgb: string;
    };
  };
  font?: {
    color: {
      rgb: string;
    };
    bold?: boolean;
  };
  numFmt?: string;
}

interface ExcelCell {
  v: string | number;
  t: 's' | 'n';
  s?: CellStyle;
}

interface StyledRow {
  [key: string]: ExcelCell;
}

interface ExcelRecord extends Record<string, unknown> {
  [key: string]: unknown;
}

const formatDecimal = (key: string, value: string | number): string => {
  if (containsKeyword(key) && isNumeric(String(value), key)) {
    let formattedValue = value.toString();
    // Nokta yerine virgül içeriyorsa, onu noktaya çevir
    if (formattedValue.includes(',')) {
      formattedValue = formattedValue.replace(',', '.');
    }
    return parseFloat(formattedValue).toFixed(2).replace('.', ',');
  }
  return String(value);
};

// Types ve interfaces
interface KeyMapping {
  statusForExel?: string;
  txnTypeForExel?: string;
  restriction?: string;
  merchantStatusType?: string;
  status?: string;
  canCall?: string;
  [key: string]: string | undefined;
}

interface Column {
  field: string;
  headerName: string;
  type?: string;
  valueOptions?: Array<{ value: string | number, label: string }>;
  valueFormatter?: (value: any) => string | number;
  filterable?: boolean;
  sortable?: boolean;
}

export const downloadExcel = (
  items: ExcelRecord[],
  fileName?: string,
  keyMapping?: KeyMapping,
  columns?: Column[]
): void => {
  const editedFileName =
    (fileName || "Dosya") +
    "_" +
    dayjs().format("DD/MM/YYYY HH:mm").toString() +
    ".xlsx";

  const ws_data = items?.map((item) => {
    const styledRow: StyledRow = {};


    let cellStyle: CellStyle | undefined = undefined; // Varsayılan stil

    const specialStatusKey = keyMapping?.["statusForExel"];
    const specialTxnTypeKey = keyMapping?.["txnTypeForExel"];
    if (specialStatusKey && specialTxnTypeKey && item[specialStatusKey] && item[specialTxnTypeKey]) {
      const status = String(item[specialStatusKey]).toUpperCase();
      const txnType = String(item[specialTxnTypeKey]).toUpperCase();
      if (status === 'VOID' && txnType === 'AUTH') {
        cellStyle = {
          fill: { patternType: "solid", fgColor: { rgb: "FF0000" } },
          font: { color: { rgb: "ffffff" } },
        };
      } else if (status === 'SUCCESS' && txnType === 'REFUND') {
        cellStyle = {
          fill: { patternType: "solid", fgColor: { rgb: "FF0000" } },
          font: { color: { rgb: "ffffff" } },
        };
      }
      else if (status === 'UNMATCHED') {
        cellStyle = {
          fill: { patternType: "solid", fgColor: { rgb: "f0f0f0" } },
          font: { color: { rgb: "333333" } },
        };
      }
    }



    if (keyMapping?.["restriction"] && item[keyMapping["restriction"]] === "Hayır") {
      cellStyle = {
        fill: { patternType: "solid", fgColor: { rgb: "FFFF0000" } },
        font: { color: { rgb: "FFFFFFFF" } },
      };
    }
    const statusKeys = ["merchantStatusType", "status", "canCall"];

    for (const statusKey of statusKeys) {
      const mappedKey = keyMapping?.[statusKey];
      if (mappedKey && item[mappedKey]) {
        const statusValue = String(item[mappedKey]);
        if (statusValue === "PASSIVE") {
          cellStyle = {
            fill: { patternType: "solid", fgColor: { rgb: "FFFF0000" } },
            font: { color: { rgb: "FFFFFFFF" } },
          };
          break;
        } else if (statusValue === "BLOCKED") {
          cellStyle = {
            fill: { patternType: "solid", fgColor: { rgb: "FFFFA500" } },
            font: { color: { rgb: "FFFFFFFF" } },
          };
          break;
        }
        else if (statusValue === "Hayır") {
          cellStyle = {
            fill: { patternType: "solid", fgColor: { rgb: "FFFFA500" } },
            font: { color: { rgb: "FFFFFFFF" } },
          };
          break;
        }
      }
    }

    for (const key in item) {
      if (key !== specialStatusKey && key !== specialTxnTypeKey) {
        let value = item[key];

        const stringValue = String(value);

        // Eğer sayısal bir değerse ve ilgili anahtar kelimeleri içeriyorsa, formatla
        if (isNumeric(stringValue, key) && containsKeyword(key)) {
          value = formatDecimal(key, stringValue);
          // Excel hücresi için sayıyı sayı olarak ata
          styledRow[key] = {
            v: value !== " " ? parseFloat(String(value).replace(',', '.')) : value, // Boş değerse boş string, değilse sayısal değer
            t: value !== " " ? "n" : "s", // Boş değerse türü string, değilse sayısal
            s: { ...cellStyle, numFmt: "#,##0.00" },
          };
        } else {
          // Değer kontrolü
          value = value ?? " "; // Excel hata vermesin diye boş string
          // Excel hücresine değeri ata
          styledRow[key] = {
            v: typeof value === 'number' ? value : String(value),
            t: typeof value === 'number' ? "n" : "s",
            s: cellStyle,
          };
        }
      }
    }
    return styledRow;
  });

  const defaultWidth = 20;
  const colWidths = [];

  let keys: string[] = [];
  if (ws_data && ws_data.length > 0 && ws_data[0]) {
    keys = Object.keys(ws_data[0]);
    for (const key of keys) {
      let maxDataLength = Math.max(
        ...ws_data?.map((row) => String(row[key]?.v).length)
      );
      let headerLength = key.length; // sütun başlığının uzunluğunu al
      let maxLength = Math.max(maxDataLength, headerLength);
      if (maxLength > defaultWidth) {
        maxLength = defaultWidth;
      }
      colWidths.push({ wch: maxLength });
    }
  }

  const workbook = XLSXC.utils.book_new();
  const worksheet = XLSXC.utils.json_to_sheet(ws_data, { cellStyles: true });

  // Hesaplanan sütun genişliklerini exele atıyoruz
  worksheet["!cols"] = colWidths;

  // Başlık stili için hücre tarzını tanımlıyoruz
  const headerStyle = {
    fill: { patternType: "solid", fgColor: { rgb: "0000A0" } }, // Başlık rengi burdan değişiyor
    font: { color: { rgb: "FFFFFF" }, bold: true },
  };

  // Başlık hücrelerini bu stille güncellenir
  if (keys.length > 0) {
    keys.forEach((key: string, idx: number) => {
      const cellRef = XLSXC.utils.encode_cell({ r: 0, c: idx });
      if (!worksheet[cellRef]) worksheet[cellRef] = {};
      worksheet[cellRef].s = headerStyle;
    });
  }

  XLSXC.utils.book_append_sheet(workbook, worksheet, `Excel`);
  XLSXC.writeFile(workbook, editedFileName);
};


function getLabelById(
  items: Array<{ value: string | number, label: string }>,
  id: string | number
): string | null {
  const item = items.find(item => item.value === id);
  return item ? item.label : null;
}

export const refactorData = (
  columns: Column[],
  data: ExcelRecord[],
  columnVisibilityModel: Record<string, boolean>,
  currentUserState?: { companyId: number }
): Record<any, unknown>[] => {
  let refactoredDataList: Record<string, unknown>[] = [];



  const visibleKeys = columns.length > 0
    ? columns.filter(
      (col: any) =>
        columnVisibilityModel[col.field] !== false &&
        col.filterable !== false &&
        col.sortable !== false
    )
    : [];
  const outIn = (data: any) => {
    //benim companyidm(3434) datanin içindeki customerId'ye eşit mi ? 
    //eşitse ilgili proccesstypenn(data.type) customerindicatoruna bakıcam 
    //değilse ilgili proccesstypenn receiverindicatoruna bakıcam 
    //-1 ise para çıkışı 1 ise para girişi
    const processType = getProccesTypesList.find(x => x.id === data?.row?.type);
    const isCustomerTransaction = data?.row?.customerId === currentUserState?.companyId; // Note: Changed = to ===
    const indicator = isCustomerTransaction ? processType?.customerIndicator : processType?.receiverIndicator;
    return indicator === -1 ? "Para Çıkışı" : "Para Girişi";
  }

  data?.forEach((item) => {
    let formattedItem: Record<string, unknown> = {};
    visibleKeys?.forEach((clmn) => {
      if (clmn.type === "datetime") {
        const isoDate = item[clmn.field];
        if (!isoDate) {
          formattedItem[clmn.headerName] = "";
        } else {
          const date = new Date(String(isoDate));
          if (isNaN(date.getTime())) {
            formattedItem[clmn.headerName] = "";
          } else {
            const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
            formattedItem[clmn.headerName] = formattedDate;
          }
        }
      } else if (clmn?.valueOptions) {
        // Boolean değerleri string'e çevir (isActive gibi kolonlar için)
        const fieldValue = item[clmn.field];
        const valueToMatch = typeof fieldValue === 'boolean' ? String(fieldValue) : fieldValue;
        formattedItem[clmn.headerName] = getLabelById(clmn.valueOptions, valueToMatch as string | number) ?? fieldValue;
      } else if (clmn?.type === "boolean") {
        formattedItem[clmn.headerName] = item[clmn.field] ? "Aktif" : "Pasif";
      } else if (clmn?.field === "transactionId") {
        
        formattedItem[clmn.headerName] = outIn({ row: item });
      } else if (clmn?.valueFormatter) {
        // valueFormatter varsa kullan - MUI DataGrid formatında
        try {
          formattedItem[clmn.headerName] = clmn.valueFormatter(item[clmn.field]);
        } catch (error) {
          formattedItem[clmn.headerName] = item[clmn.field];
        }
      } else {
        formattedItem[clmn.headerName] = item[clmn.field];
      }
    });

    refactoredDataList.push(formattedItem);
  });
  return refactoredDataList;
}